import draftJs from "draft-js";
import handlebars from "handlebars";
import { draftStateToHtml } from "../functions.js";
import { PLACEHOLDER_REGISTRY, availablePlaceholders } from "./registry.js";
const { convertFromRaw } = draftJs;
// Checks whether a user-editable template body is syntactically valid Handlebars,
// so a body that would abort the whole render (e.g. an unreplaced
// `{{…<prop_key>…}}` example, whose `<`/`>` are illegal in a path) is never
// persisted. Uses `Handlebars.parse` — exactly the parse step that the renderer's
// lazy `compile` defers to first execution — so no context or rendering is needed
// and valid templates (including ones using helpers) are never falsely rejected.
//
// Bodies stored as Draft.js raw content state (quote / document texts) are first
// converted to the text the renderer compiles; plain HTML / string bodies (email
// templates) are parsed as-is. Never throws — the outcome is the return value.
export function validateTemplate(body) {
    if (!body)
        return { valid: true };
    const source = toTemplateSource(body);
    try {
        handlebars.parse(source);
        return { valid: true };
    }
    catch (e) {
        return { valid: false, error: e instanceof Error ? e.message : String(e) };
    }
}
// Reports every placeholder token in a body that is not available in `scope`,
// so a token that is merely *syntactically* valid — Handlebars resolves an
// unknown path to the empty string — does not reach a rendered email as a hole.
//
// Works on the `handlebars.parse` AST rather than matching the body text: the
// parser is what already knows a comment from a token, a helper invocation from
// a bare path, and `{{{x}}}` / `{{ x }}` from `{{x}}`. A token is a
// `MustacheStatement` whose path is a `PathExpression` with no params,
// `data === false` and `depth === 0`; block statements are descended into so
// nested tokens are still checked.
//
// Tokens are deduplicated by path and reported in first-appearance order — a
// body holding several stale tokens is fixed in one pass rather than one save
// attempt per token. Never throws: an unparseable body reports nothing and is
// left to `validateTemplate`, which reports that failure better.
//
// Parses the body itself rather than taking an AST from `validateTemplate`: the
// template editor runs this check on its own, with no syntax validation ahead
// of it, so there is not always an earlier parse to take. The save path pays
// for a second parse of a short subject or body once per save.
//
// The registry's `<prop_key>` wildcard entries are matched literally, so this is
// only meaningful for scopes with no wildcard entries — every wildcard entry is
// `channels: ["document"]`, so the email scopes are a closed exact set.
export function findUnavailablePlaceholders(body, scope) {
    if (!body)
        return [];
    let program;
    try {
        program = handlebars.parse(toTemplateSource(body));
    }
    catch {
        return [];
    }
    const available = new Set(availablePlaceholders(scope).map(entry => entry.id));
    const inRegistry = new Set(PLACEHOLDER_REGISTRY.map(entry => entry.id));
    const reported = new Set();
    const unavailable = [];
    for (const token of collectTokenPaths(program)) {
        if (available.has(token) || reported.has(token))
            continue;
        reported.add(token);
        unavailable.push({
            token,
            reason: inRegistry.has(token) ? "outOfScope" : "unknown",
        });
    }
    return unavailable;
}
// Mirrors the renderer: a Draft.js raw content state JSON is rendered to text
// before parsing; anything else is parsed directly.
function toTemplateSource(body) {
    try {
        const parsed = JSON.parse(body);
        if (parsed &&
            typeof parsed === "object" &&
            Array.isArray(parsed.blocks)) {
            return draftStateToHtml(convertFromRaw(parsed));
        }
    }
    catch {
        // not JSON — treat as a plain HTML / string body
    }
    return body;
}
function collectTokenPaths(program) {
    const paths = [];
    for (const statement of program.body) {
        if (isMustacheStatement(statement)) {
            const { path, params } = statement;
            if (params.length === 0 &&
                isPathExpression(path) &&
                !path.data &&
                path.depth === 0) {
                paths.push(path.original);
            }
        }
        else if (isBlockStatement(statement)) {
            if (statement.program)
                paths.push(...collectTokenPaths(statement.program));
            if (statement.inverse)
                paths.push(...collectTokenPaths(statement.inverse));
        }
    }
    return paths;
}
// `hbs.AST.Statement` is a plain `{ type: string }` base rather than a
// discriminated union, so narrowing needs explicit predicates.
function isMustacheStatement(node) {
    return node.type === "MustacheStatement";
}
function isBlockStatement(node) {
    return node.type === "BlockStatement";
}
function isPathExpression(node) {
    return node.type === "PathExpression";
}
//# sourceMappingURL=validate.js.map